import React, { useMemo } from 'react'
import { subscribe } from 'react-contextual'
import { concat, filter as rFilter, map, path, pathOr, pipe } from 'ramda'
import { format, getUnixTime, isPast, isValid } from 'date-fns'
import styles from './PortfoliosTable.module.scss'
import { stringUtils } from '../../../../utils'
import { formatCurrency } from '../../../../utils/currency'
import { SearchFilterProvider } from '../../../../views/providers'
import PortfoliosProvider from '../../PortfoliosProvider'
import { AggregateTable, NoContent } from '../../../../views/components'
import { TSortableHeaderProps } from '../../../../views/components/organisms/Table/AggregateTable'
import NameCell from './NameCell'
import StatusCell from './StatusCell'
import LeaseTermHeader from './LeaseTermHeader'
import { ItemListCell } from '../../../../views/components/organisms/Table'
import { applyLeaseFilterPills } from '../../filters'
import { getLeaseStatusRank } from '../../leaseStatus'
import { useLeaseFilterPills } from '../../useLeaseFilterPills'
import { $TSFixMe } from 'types/ts-migrate'

/** Meta keys the table sorts on. The lease term column owns two of them. */
const SORT_KEYS = {
  status: 'status',
  propertyAddress: 'propertyAddress',
  owner: 'owner',
  tenant: 'tenant',
  applicant: 'applicant',
  leaseStart: 'leaseStart',
  leaseEnd: 'leaseEnd',
  applications: 'applications',
}

const statusColumn = {
  Header: 'Status',
  accessor: 'status',
  style: {
    maxWidth: '160px',
    justifyContent: 'flex-start',
    paddingLeft: '16px',
  },
  sort: {
    enabled: true,
    // The All tab opens in status order: what needs doing first, then what is only history.
    order: 'asc',
  },
}

const propertyColumn = {
  Header: 'Property',
  accessor: 'propertyAddress',
  style: {
    minWidth: '320px',
    textAlign: 'left',
    justifyContent: 'flex-start',
    paddingLeft: '16px',
  },
  sort: {
    enabled: true,
    order: false,
  },
}

const ownerColumn = {
  Header: 'Owner',
  accessor: 'owner',
  sort: {
    enabled: true,
    order: false,
  },
}

const tenantColumn = {
  Header: 'Tenant',
  accessor: 'tenant',
  sort: {
    enabled: true,
    order: false,
  },
}

const applicationsColumn = {
  Header: 'Applications',
  accessor: 'applications',
  sort: {
    enabled: true,
    order: false,
  },
}

const getLeaseTermColumn = (isDefaultSort: boolean, switchSortOrder: boolean): $TSFixMe => ({
  Header: 'Lease term',
  accessor: 'leaseTerm',
  renderHeader: (props: TSortableHeaderProps) => (
    <LeaseTermHeader {...props} startKey={SORT_KEYS.leaseStart} endKey={SORT_KEYS.leaseEnd} />
  ),
  sort: {
    enabled: true,
    key: SORT_KEYS.leaseEnd,
    // A status tab drops the status column — every row on it shares one status — so the end
    // date takes over as the default sort. Terminated leases open most recent first.
    order: isDefaultSort ? (switchSortOrder ? 'desc' : 'asc') : false,
  },
})

const formatDate = (value?: string): string => {
  const date = value ? new Date(value) : undefined
  return date && isValid(date) ? format(date, 'dd MMM yyyy') : ''
}

/** Unix seconds as a string, which is what the table's meta comparison expects. */
const toSortableTime = (value?: string): string => {
  const date = value ? new Date(value) : undefined
  return date && isValid(date) ? getUnixTime(date).toString() : ''
}

/** Applicants carry no primary flag, so they keep the order the summary sends them in. */
const applicantNames = (applications?: $TSFixMe[]): string[] =>
  (applications ?? []).map(a => a?.partyName).filter(Boolean)

const orderedParties = (parties: any): $TSFixMe =>
  pipe(
    // @ts-expect-error
    rFilter((t: any) => !t.isPrimary),
    concat(rFilter((t: any) => t.isPrimary)(parties)),
    map((t: any) => t.name),
  )(parties)

const PortfoliosTable = ({
  filters,
  openPortfolio,
  portfolioViewId,
  primaryPanel,
  searchQuery,
  activeFilterIndex,
}: any): React.ReactElement => {
  const filter: $TSFixMe = pathOr({}, [activeFilterIndex], filters)
  const tabKey: string = pathOr('', ['key'], filter)
  const { selection } = useLeaseFilterPills()

  const isPrimaryPanelOpen = Boolean(path(['open'], primaryPanel))
  // Every row on a status tab carries the same status, so the column would say nothing; dropping
  // it hands the width to the columns that do.
  const showStatus = tabKey === 'all'
  const showApplications = tabKey === 'draft'
  // A terminated lease ends on its termination date, and the rent no longer matters, so the term
  // runs to that date and the meta line says when the tenant moves out.
  const isTerminated = tabKey === 'terminated'
  const isExpired = tabKey === 'expired'

  const data = useMemo(
    () =>
      applyLeaseFilterPills(filter.portfolios ?? [], selection, { tabKey }).map((row: $TSFixMe) => ({
        ...row,
        // Fields the table sorts and searches on.
        meta: {
          [SORT_KEYS.status]: getLeaseStatusRank(row.status),
          [SORT_KEYS.propertyAddress]: row.mainText ?? '',
          [SORT_KEYS.owner]: row.primaryOwnerName ?? '',
          [SORT_KEYS.tenant]: row.primaryTenantName ?? '',
          [SORT_KEYS.leaseStart]: toSortableTime(row.leaseTerms?.startDate),
          [SORT_KEYS.leaseEnd]: toSortableTime(isTerminated ? row.terminatedAt : row.leaseTerms?.endDate),
          [SORT_KEYS.applications]: applicantNames(row.applications).join(', '),
        },
      })),
    [filter.portfolios, selection, tabKey, isTerminated],
  )

  const columns = useMemo(
    () =>
      isPrimaryPanelOpen
        ? [propertyColumn]
        : [
            ...(showStatus ? [statusColumn] : []),
            propertyColumn,
            ownerColumn,
            tenantColumn,
            ...(showApplications ? [applicationsColumn] : []),
            getLeaseTermColumn(!showStatus, isTerminated || isExpired),
          ],
    [isPrimaryPanelOpen, showStatus, showApplications, isTerminated, isExpired],
  )

  const portfoliosData = useMemo(() => {
    const buildLeaseObj = (p: $TSFixMe): $TSFixMe => {
      const startDate = formatDate(p.leaseTerms?.startDate)
      const endDate = formatDate(isTerminated ? p.terminatedAt : p.leaseTerms?.endDate)
      const endNode = isTerminated && endDate ? <span className={styles.terminatedDate}>{endDate}</span> : endDate
      // A month-to-month lease keeps the end date of the term it rolled over from.
      const leaseTerm =
        startDate && endDate ? (
          <>
            {startDate} → {endNode}
          </>
        ) : (
          startDate || endNode
        )
      const propertyAddress = p.metaData ? `${p.metaData as string}, ${p.mainText as string}` : p.mainText
      const rent = p.monthlyRentAmount ? formatCurrency(p.monthlyRentAmount) : ''
      const vacateDate = formatDate(p.vacateDate)
      const vacates = vacateDate ? `${isPast(new Date(p.vacateDate)) ? 'Vacated' : 'Vacates'} ${vacateDate}` : ''
      const termMeta = isTerminated ? vacates : rent

      return {
        id: p.portfolioId,
        meta: p.meta,
        highlighted: portfolioViewId === p.portfolioId,
        ...(showStatus && { status: <StatusCell status={p.status} /> }),
        propertyAddress: <NameCell name={propertyAddress} meta={p.secondaryText} />,
        owner: <ItemListCell items={orderedParties(p.landlords)} />,
        tenant: <ItemListCell items={orderedParties(p.tenants)} />,
        ...(showApplications && { applications: <ItemListCell items={applicantNames(p.applications)} /> }),
        leaseTerm: <NameCell name={leaseTerm} meta={[p.leaseType, termMeta].filter(Boolean).join(' · ')} />,
      }
    }

    const rows = searchQuery ? data.filter((p: $TSFixMe) => stringUtils.jsonStringSearch(searchQuery, p)) : data

    return map(buildLeaseObj, rows)
  }, [data, searchQuery, portfolioViewId, showStatus, showApplications, isTerminated])

  return useMemo(
    () => (
      <div className={styles.root}>
        {portfoliosData.length > 0 ? (
          <AggregateTable
            rowHeight={80}
            columns={columns}
            data={portfoliosData}
            onRowClick={data => openPortfolio(data.id)}
            index={activeFilterIndex}
          />
        ) : (
          <NoContent heading={<p>No leases for selected filters.</p>} />
        )}
      </div>
    ),
    [columns, portfoliosData, openPortfolio, activeFilterIndex],
  )
}

export default subscribe(
  [PortfoliosProvider, SearchFilterProvider],
  ({ filters, openPortfolio, portfolioViewId, primaryPanel }: any, { searchQuery, activeFilterIndex }: any) => ({
    filters,
    openPortfolio,
    portfolioViewId,
    primaryPanel,
    searchQuery,
    activeFilterIndex,
  }),
)(PortfoliosTable)
